import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { findTruncatedText } from "../../utils/textTruncation";

const SHOW_DELAY_MS = 150;
const OFFSET_PX = 6;
const VIEWPORT_MARGIN_PX = 8;
const ARROW_EDGE_PX = 12;

const clamp = (value, min, max) => Math.min(Math.max(value, min), Math.max(min, max));

// Centers the tooltip under the target (above it when there is no room),
// kept inside the viewport, with the arrow pointing at the target center.
const computePosition = (targetRect, tooltipWidth, tooltipHeight) => {
  const center = targetRect.left + targetRect.width / 2;
  const left = clamp(center - tooltipWidth / 2, VIEWPORT_MARGIN_PX, window.innerWidth - VIEWPORT_MARGIN_PX - tooltipWidth);
  const fitsBelow = targetRect.bottom + OFFSET_PX + tooltipHeight <= window.innerHeight - VIEWPORT_MARGIN_PX;
  return {
    placement: fitsBelow ? "bottom" : "top",
    top: fitsBelow ? targetRect.bottom + OFFSET_PX : targetRect.top - OFFSET_PX - tooltipHeight,
    left,
    arrowLeft: clamp(center - left, ARROW_EDGE_PX, tooltipWidth - ARROW_EDGE_PX),
  };
};

/**
 * App-wide tooltip for text cut by CSS (text-overflow: ellipsis or line clamp).
 * It shows the full text only while the hovered text is actually truncated,
 * and stays out of the way of elements that already have a tooltip.
 */
function TruncationTooltip() {
  const [target, setTarget] = useState(null);
  const [position, setPosition] = useState(null);
  const tooltipRef = useRef(null);
  const currentElementRef = useRef(null);
  const showTimerRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined" || window.matchMedia?.("(hover: none)").matches) return undefined;

    const hide = () => {
      window.clearTimeout(showTimerRef.current);
      currentElementRef.current = null;
      setTarget(null);
      setPosition(null);
    };

    const onMouseOver = (event) => {
      const current = currentElementRef.current;
      if (current && current.contains(event.target)) return;

      const match = findTruncatedText(event.target);
      if (!match) {
        if (current) hide();
        return;
      }

      hide();
      currentElementRef.current = match.element;
      showTimerRef.current = window.setTimeout(() => {
        if (currentElementRef.current !== match.element || !match.element.isConnected) return;
        // A component tooltip (e.g. PrimeReact) already open for this element wins.
        if (document.querySelector(".p-tooltip")) return;
        setTarget(match);
      }, SHOW_DELAY_MS);
    };

    const onMouseOut = (event) => {
      const current = currentElementRef.current;
      if (current && !current.contains(event.relatedTarget)) hide();
    };

    // PrimeReact `<Tooltip target=...>` leaves no marker on its target, so a
    // component tooltip may open after this one is already visible (e.g. the
    // pointer moves from a truncated cell onto a link inside it). Yield to it.
    const isComponentTooltip = (node) =>
      node.nodeType === 1 && (node.matches?.(".p-tooltip") || Boolean(node.querySelector?.(".p-tooltip")));
    const observer = typeof MutationObserver === "function"
      ? new MutationObserver((mutations) => {
          if (!currentElementRef.current) return;
          const opened = mutations.some((mutation) => Array.from(mutation.addedNodes).some(isComponentTooltip));
          if (opened) hide();
        })
      : null;
    observer?.observe(document.body, { childList: true });

    document.addEventListener("mouseover", onMouseOver);
    document.addEventListener("mouseout", onMouseOut);
    document.addEventListener("mousedown", hide);
    window.addEventListener("scroll", hide, true);
    window.addEventListener("resize", hide);

    return () => {
      window.clearTimeout(showTimerRef.current);
      observer?.disconnect();
      document.removeEventListener("mouseover", onMouseOver);
      document.removeEventListener("mouseout", onMouseOut);
      document.removeEventListener("mousedown", hide);
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("resize", hide);
    };
  }, []);

  useLayoutEffect(() => {
    if (!target || !tooltipRef.current) return;
    const { width, height } = tooltipRef.current.getBoundingClientRect();
    setPosition(computePosition(target.element.getBoundingClientRect(), width, height));
  }, [target]);

  if (!target) return null;

  return createPortal(
    <div
      ref={tooltipRef}
      className={`truncation-tooltip truncation-tooltip-${position?.placement || "bottom"}${position ? " is-visible" : ""}`}
      role="tooltip"
      style={position ? { top: position.top, left: position.left } : undefined}
    >
      <span className="truncation-tooltip-arrow" style={position ? { left: position.arrowLeft } : undefined} />
      <div className="truncation-tooltip-text">{target.text}</div>
    </div>,
    document.body
  );
}

export default TruncationTooltip;
